import {
  LicenseSortField,
  LicenseTypeInput,
  PaginationSortingDirection,
  useLicensesQuery
} from "generated/graphql";
import {
  useQueryParamObjectWith,
  useQueryParamString,
  useRouteMatch
} from "polyrouter";
import { SelectedTenantContext } from "polyrouter/SelectedTenantProvider";
import { useContext, useEffect, useState } from "react";
import { formatDate } from "routes/Manage/Inventory/View/DeviceDetails";
import { useTranslation } from "utilities/i18n";
import { useSearchTerm } from "utilities/search";

const PAGE_SIZE = 100;

export type FormattedLicense = {
  licenseKey: string;
  type: string;
  activationDate: string;
  endDate: string;
  quantity: string;
  userId: string;
};

type SortField =
  | "licenseKey"
  | "quantity"
  | "activationDate"
  | "endDate"
  | "userId";

type SortState = {
  field: SortField;
  direction: PaginationSortingDirection;
};

const fieldMappings = {
  licenseKey: LicenseSortField.LicenseKey,
  quantity: LicenseSortField.Quantity,
  activationDate: LicenseSortField.ActivationDate,
  endDate: LicenseSortField.EndDate,
  userId: LicenseSortField.UserId
};

export const useLicenseTypeFromRoute = () => {
  let type;
  if (!!useRouteMatch("/manage/licensing/clariti/license-keys")) {
    type = LicenseTypeInput.Clariti;
  }
  if (!!useRouteMatch("/manage/licensing/poly-plus/license-keys")) {
    type = LicenseTypeInput.PolyPlus;
  }
  if (!!useRouteMatch("/manage/licensing/poly-plus/mock-licenses")) {
    type = LicenseTypeInput.MockOnly;
  }
  return type;
};

export const useFormattedLicenses = () => {
  const { t } = useTranslation();
  const { tenant } = useContext(SelectedTenantContext);
  const tenantId = tenant?.id ?? "";
  const typeRoute = useLicenseTypeFromRoute();
  const typeFilter = useQueryParamString("type")[0] as LicenseTypeInput;
  const [currentSort] = useQueryParamObjectWith<"sort", SortState>("sort", {
    field: "licenseKey",
    direction: PaginationSortingDirection.Asc
  });
  const [searchTerm] = useSearchTerm();
  const [from, setFrom] = useState(0);

  const { data, fetching, stale } = useLicensesQuery({
    variables: {
      tenantId: tenantId,
      type: typeRoute ?? typeFilter,
      searchTerm,
      canceled: false,
      limit: PAGE_SIZE,
      from,
      sort: {
        field: fieldMappings[currentSort.field],
        direction: currentSort.direction
      }
    }
  });

  useEffect(() => {
    setFrom(0);
  }, [searchTerm, typeFilter, currentSort.direction, currentSort.field]);

  const updateCursor = (index: number) => {
    if (
      data &&
      index === data.licenses.length - 1 &&
      data.licenses.length < data.count
    ) {
      setFrom(from => from + PAGE_SIZE);
    }
  };

  const licenses =
    data?.licenses.map(license => ({
      licenseKey: license.licenseKey ?? "",
      type: t(license.type),
      activationDate: formatDate(license.activationDate, "P"),
      endDate: formatDate(license.endDate, "P"),
      quantity: license.quantity?.toString() ?? "",
      userId: license.userId ?? ""
    })) ?? [];

  const loading = fetching || (stale && licenses.length === 0);

  return { licenses, loading, updateCursor };
};
